import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { ShowToast } from "../../components/toast";
import { competitorKeywordsKey } from "./use-competitor-keywords";
import type {
  CompetitorStepAnswers,
  CompetitorStepSnapshot,
} from "../../types/competitors";

const competitorStepKey = (userId: string) => ["competitor-step", userId];

function hotelLocation(metadata: any): CompetitorStepSnapshot["location"] {
  if (metadata?.latitude == null || metadata?.longitude == null) return null;
  const lat = Number(metadata.latitude);
  const lng = Number(metadata.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function toSnapshot(onboarding: any): CompetitorStepSnapshot {
  const metadata = onboarding?.userMetadata ?? {};
  return {
    competitors: Array.isArray(metadata.competitors) ? metadata.competitors : [],
    usp: metadata.usp ?? "",
    amenities: metadata.amenities ?? "",
    hotelName: metadata.hotelName ?? null,
    googlePlaceId: metadata.googlePlaceId ?? null,
    location: hotelLocation(metadata),
    competitorLocationRating: metadata.competitorLocationRating ?? null,
    competitorQualityRating: metadata.competitorQualityRating ?? null,
    competitorPrices: metadata.competitorPrices ?? null,
  };
}

/** The Competitor step's answers on a hotel's onboarding record, with what
 *  the editor needs to search near the hotel and to save cleanly. */
export function useCompetitorStep(userId: string) {
  return useQuery<CompetitorStepSnapshot>({
    queryKey: competitorStepKey(userId),
    queryFn: () => api.getOnboarding(userId).then(toSnapshot),
    enabled: !!userId,
    refetchOnWindowFocus: false,
  });
}

/**
 * Saves through the endpoint the customer's own form uses, so an operator's
 * save gets everything a customer's does: rate-shop codes looked up for new
 * hotels, the competitor change posted to Slack, and — for a hotel that has
 * finished onboarding — the compset rebuilt and rates re-synced. The
 * customer's rating answers ride along unchanged so the change notice does
 * not read them as removed.
 */
export function useSaveCompetitorStep(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (answers: CompetitorStepAnswers) => {
      const snapshot = queryClient.getQueryData<CompetitorStepSnapshot>(
        competitorStepKey(userId),
      );
      return api.saveOnboarding(userId, {
        ...answers,
        competitorLocationRating:
          snapshot?.competitorLocationRating ?? undefined,
        competitorQualityRating: snapshot?.competitorQualityRating ?? undefined,
        competitorPrices: snapshot?.competitorPrices ?? undefined,
      });
    },
    onSuccess: () => {
      ShowToast({ type: "success", message: "Onboarding answers saved" });
      queryClient.invalidateQueries({ queryKey: competitorStepKey(userId) });
      // The Google Ads card compares the saved set against the live ad group.
      queryClient.invalidateQueries({ queryKey: competitorKeywordsKey(userId) });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error?.response?.data?.message ?? "Failed to save onboarding answers",
      });
    },
  });
}
